/**
 * BroadcastProvider — cross-tab Yjs sync over BroadcastChannel.
 *
 * Beyond forwarding live updates it does a SYNC HANDSHAKE so a newly-opened tab
 * (which, under leader election, does NOT open OPFS itself — see useEditorDoc)
 * bootstraps its doc from an already-open tab:
 *
 *   • On construct it broadcasts `query`. A peer that HAS state answers with
 *     `state` (after a small jitter; the first answer cancels the others, so N
 *     open tabs don't all reply to one query — see the response dedupe below).
 *   • The persistence leader calls `broadcastState()` once it has finished
 *     replaying OPFS, so a follower that queried BEFORE the replay converges and
 *     its `onSynced` fires. This replaces re-broadcasting the replay update by
 *     update (which flooded the channel with a whole history) — the leader's
 *     replay is tagged LOCAL_PERSIST_ORIGIN and is deliberately NOT forwarded.
 *   • `onSynced` fires the first time `state` arrives from a peer, so the editor
 *     can gate `loaded` on it for follower tabs.
 *
 * The channel is injectable (BroadcastLike) so the handshake is unit-testable
 * with an in-process bus standing in for multiple tabs.
 */
import * as Y from "yjs";

/**
 * The persistence provider tags its OPFS replay with this origin. We recreate it
 * from the SAME global-registry key rather than importing the provider package,
 * so this module carries no sqlite-wasm/worker weight. MUST stay in sync with
 * `LOCAL_PERSIST_ORIGIN` in @fredericrous/yjs-sqlite-provider.
 */
const LOCAL_PERSIST_ORIGIN = Symbol.for(
  "@website-builder/yjs-sqlite-provider/local-persist",
);

/** Cross-tab messages. `query` asks open tabs for the doc; `state` answers with a
 *  full snapshot; `update` forwards a live edit. Uint8Array crosses via
 *  structured clone. */
type BroadcastMsg =
  | { t: "query" }
  | { t: "state"; u: Uint8Array }
  | { t: "update"; u: Uint8Array };

/** The slice of BroadcastChannel we use — lets tests inject a shared fake bus.
 *  `onmessage` takes a MessageEvent so a real BroadcastChannel is assignable. */
export interface BroadcastLike {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((ev: MessageEvent) => void) | null;
}

export interface BroadcastProviderOptions {
  /** Fired the first time a peer hands us `state` (a follower is now synced). */
  onSynced?: () => void;
  /** Test seam: ms to wait before answering a `query`. A peer's answer cancels
   *  our pending one, so staggering caps a query to ~one responder. Default
   *  random 0–25ms. */
  responseJitterMs?: () => number;
  /** Test seam: supply the channel instead of `new BroadcastChannel`. */
  channel?: BroadcastLike;
}

export class BroadcastProvider {
  private bc: BroadcastLike;
  private ydoc: Y.Doc;
  private onSynced?: () => void;
  private synced = false;
  private jitter: () => number;
  private pendingReply: ReturnType<typeof setTimeout> | null = null;

  constructor(ydoc: Y.Doc, roomName: string, opts?: BroadcastProviderOptions) {
    this.ydoc = ydoc;
    this.onSynced = opts?.onSynced;
    this.jitter = opts?.responseJitterMs ?? (() => Math.random() * 25);
    this.bc = opts?.channel ?? new BroadcastChannel(`wb:${roomName}`);

    ydoc.on("update", this.handleLocalUpdate);
    this.bc.onmessage = this.handleMessage;

    // Ask any open tab for the current doc. We do NOT broadcast our own state
    // unsolicited: at construct the doc is always empty (it's created just
    // before this provider), and the authoritative push comes from the leader's
    // broadcastState() once it has replayed.
    this.post({ t: "query" });
  }

  /**
   * Push the full doc to peers. Called by the persistence leader once it has
   * replayed OPFS, so tabs that queried before the replay converge (and their
   * onSynced fires). No-op when the doc is empty (encodes to ≤2 bytes).
   */
  broadcastState(): void {
    const state = Y.encodeStateAsUpdate(this.ydoc);
    if (state.length > 2) this.post({ t: "state", u: state });
  }

  private post(m: BroadcastMsg): void {
    this.bc.postMessage(m);
  }

  private handleLocalUpdate = (update: Uint8Array, origin: unknown): void => {
    // Skip the echo of updates we just applied FROM the channel (origin === bc),
    // and the leader's OPFS REPLAY (LOCAL_PERSIST_ORIGIN) — bootstrap goes via
    // query/response + broadcastState, so a long replayed history no longer
    // floods every tab update-by-update.
    if (origin === this.bc || origin === LOCAL_PERSIST_ORIGIN) return;
    this.post({ t: "update", u: update });
  };

  private handleMessage = (event: MessageEvent): void => {
    const m = event.data as BroadcastMsg;
    if (m.t === "query") {
      // Answer only if we have state, and only if nobody beats us to it. A small
      // jitter staggers responders; the first `state` on the wire cancels the
      // rest (see the `state` branch), so one query costs ~one reply, not N.
      if (this.pendingReply) return;
      const state = Y.encodeStateAsUpdate(this.ydoc);
      if (state.length <= 2) return;
      this.pendingReply = setTimeout(() => {
        this.pendingReply = null;
        this.post({ t: "state", u: state });
      }, this.jitter());
    } else if (m.t === "state") {
      // A peer answered — cancel our own pending reply (response dedupe).
      if (this.pendingReply) {
        clearTimeout(this.pendingReply);
        this.pendingReply = null;
      }
      Y.applyUpdate(this.ydoc, m.u, this.bc);
      if (!this.synced) {
        this.synced = true;
        this.onSynced?.();
      }
    } else if (m.t === "update") {
      Y.applyUpdate(this.ydoc, m.u, this.bc);
    }
  };

  destroy(): void {
    if (this.pendingReply) {
      clearTimeout(this.pendingReply);
      this.pendingReply = null;
    }
    this.ydoc.off("update", this.handleLocalUpdate);
    this.bc.close();
  }
}
